/**
 * TestMailDialog — send one schedule's REAL report to an internal address.
 *
 * The gap this closes: `dry-run` shows the resolved source key, the recipients and the
 * exact column list, but it does not render the mail. So the first time anyone sees the
 * subject line, the outcomes table, the link wording or the expiry sentence for a newly
 * configured client is in that client's inbox.
 *
 * Everything except the destination comes from the schedule row — client, shape, window,
 * flow, campaign, opt-in columns, format. A test assembled from hand-passed parameters
 * tests an ask that will never fire; this tests the thing that will actually run, which is
 * why a client with no schedule yet gets a disabled one created and tested rather than a
 * form of its own.
 *
 * Two things this dialog must not pretend about:
 *
 *  - **The allow-list is not ours.** `TEST_MAIL_ALLOWED_DOMAINS` is enforced in
 *    `services/api/validate.py` and again in the dispatcher, because the mail carries real
 *    borrower data for a named lender and a browser check is bypassed by a `curl`. This
 *    form states the rule and shows the server's refusal verbatim; it never decides.
 *  - **202 is not "sent".** The dispatcher invoke is asynchronous, so at the moment the
 *    response lands nothing has been rendered and nothing has left. The honest result is
 *    the one the run reports, so this polls the run and shows the message id only once
 *    there is one — the same thing run-now does, for the same reason.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { Field, Input } from '@/components/Field';
import Modal from '@/components/Modal';
import { useToast } from '@/components/Toast';
import { isValidEmail } from '@/lib/addresses';
import { api, errorMessage, isApiError } from '@/lib/api';
import { getIdentity } from '@/lib/auth';
import { describeWindow, istDateTime, pluralise } from '@/lib/format';
import { RUN_IN_FLIGHT, TEST_MAIL_LINK_TTL_DAYS } from '@/lib/types';
import type { Client, Run, Schedule, TestMailRejection, TestMailResult } from '@/lib/types';

/** How often the run is re-read while it is still moving, and when to stop asking. */
const POLL_MS = 2500;
const MAX_POLLS = 40;

export interface TestMailDialogProps {
  open: boolean;
  schedule: Schedule | null;
  /** For the lender's name and its link TTL, both of which appear in the warning. */
  client?: Client | null;
  onClose: () => void;
}

export function TestMailDialog({ open, schedule, client, onClose }: TestMailDialogProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState<TestMailResult | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** Named by the server when it refuses. There is no route that lists them up front. */
  const [allowedDomains, setAllowedDomains] = useState<string[] | null>(null);

  const seeded = useRef(false);

  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (seeded.current) return;
    seeded.current = true;
    // The operator's own address, which is checked by the same allow-list as any other —
    // a console user on an external domain is refused like anyone else.
    setTo(getIdentity()?.email ?? '');
    setSending(false);
    setQueued(null);
    setRun(null);
    setGaveUp(false);
    setRefusal(null);
    setAllowedDomains(null);
  }, [open]);

  // Follow the run until it stops moving. A test that fails in generate is still a test,
  // and "202 Accepted" on its own would report a successful test of a mail nobody received.
  useEffect(() => {
    if (!queued) return;
    let cancelled = false;
    let attempts = 0;
    let timer = 0;

    const tick = async (): Promise<void> => {
      attempts += 1;
      try {
        const latest = await api.runs.get(queued.run_id);
        if (cancelled) return;
        setRun(latest);
        if (!(RUN_IN_FLIGHT as readonly string[]).includes(latest.status)) return;
      } catch {
        // A poll that fails is not a send that failed. Keep asking; the run row is the
        // authority and the operator can open it directly either way.
        if (cancelled) return;
      }
      if (attempts >= MAX_POLLS) {
        setGaveUp(true);
        return;
      }
      timer = window.setTimeout(() => void tick(), POLL_MS);
    };

    timer = window.setTimeout(() => void tick(), 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [queued]);

  if (!schedule) return null;

  const lender = client?.company_name || client?.name || schedule.client_id;
  const address = to.trim();
  const addressProblem = address.length === 0
    ? 'Enter the internal address this test should go to.'
    : !isValidEmail(address)
      ? 'That does not look like an email address.'
      : null;

  const messageId = run?.ses_message_ids?.[0] ?? null;
  const inFlight = run !== null && (RUN_IN_FLIGHT as readonly string[]).includes(run.status);
  const done = run !== null && !inFlight;

  const send = async (): Promise<void> => {
    if (addressProblem || sending) return;
    setSending(true);
    setRefusal(null);
    setAllowedDomains(null);
    try {
      const accepted = await api.schedules.testMail(schedule.schedule_id, { to: address });
      setQueued(accepted);
      toast.success('Test mail queued', {
        description: `Run ${accepted.run_id} is building the real report for ${lender}.`,
      });
    } catch (err) {
      setRefusal(errorMessage(err));
      if (isApiError(err) && err.details && typeof err.details === 'object') {
        const detail = err.details as TestMailRejection;
        if (Array.isArray(detail.allowed_domains)) setAllowedDomains(detail.allowed_domains);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!sending}
      size="sm"
      title="Send a test"
      description={
        queued
          ? 'The real report, to one internal address. Follow it here or on the run.'
          : `${schedule.name ?? schedule.schedule_id} — exactly what ${lender} would receive.`
      }
      footer={
        <>
          <Button variant="ghost" disabled={sending} onClick={onClose}>
            {queued ? 'Close' : 'Cancel'}
          </Button>
          {queued ? (
            <Button
              variant="primary"
              onClick={() => {
                onClose();
                navigate(`/runs/${queued.run_id}`);
              }}
            >
              Open run
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={sending}
              disabled={Boolean(addressProblem)}
              title={addressProblem ?? undefined}
              onClick={() => void send()}
            >
              Send test mail
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        {queued ? null : (
          <>
            <Field
              label="Send to"
              required
              error={address.length > 0 ? addressProblem : null}
              hint={
                allowedDomains && allowedDomains.length > 0
                  ? `Internal addresses only: ${allowedDomains.join(', ')}.`
                  : 'Internal addresses only. The allow-list is enforced on the server, not in this form — a test mail can never reach a client.'
              }
            >
              {(control) => (
                <Input
                  {...control}
                  mono
                  type="email"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={sending}
                  invalid={address.length > 0 && Boolean(addressProblem)}
                  value={to}
                  onChange={(event) => {
                    setTo(event.target.value);
                    setRefusal(null);
                  }}
                />
              )}
            </Field>

            <div className="callout tone-warn">
              <span className="callout-glyph" aria-hidden="true">
                ▲
              </span>
              <span>
                This is the <strong>real report</strong> for <strong>{lender}</strong> —{' '}
                {describeWindow(schedule.window).toLowerCase()}, built from real borrower data.
                The download links in it work. They expire in{' '}
                {pluralise(TEST_MAIL_LINK_TTL_DAYS, 'day')} rather than the client’s{' '}
                {pluralise(client?.link_ttl_days ?? 7, 'day')}, the subject is marked as a test,
                and the send is left out of this client’s delivery figures. Do not forward it.
              </span>
            </div>
          </>
        )}

        {queued ? (
          <div className="stack-tight">
            <dl className="kv">
              <dt>Run</dt>
              <dd className="mono">{queued.run_id}</dd>
              <dt>To</dt>
              <dd className="mono">{queued.to}</dd>
              <dt>Links expire</dt>
              <dd>{pluralise(queued.link_ttl_days ?? TEST_MAIL_LINK_TTL_DAYS, 'day')} after send</dd>
              {queued.window_label ? (
                <>
                  <dt>Window</dt>
                  <dd>{queued.window_label}</dd>
                </>
              ) : null}
              <dt>Status</dt>
              <dd>
                {run ? run.status : queued.status}
                {run?.started_at ? (
                  <span className="xs dim"> · started {istDateTime(run.started_at)}</span>
                ) : null}
              </dd>
            </dl>

            {inFlight || !run ? (
              <div className="callout tone-info">
                <span className="callout-glyph" aria-hidden="true">
                  i
                </span>
                <span>
                  Accepted, not sent. The report is being built from the real source object now;
                  this updates when the run finishes.
                </span>
              </div>
            ) : null}

            {done && run?.status === 'sent' ? (
              <div className="callout tone-info">
                <span className="callout-glyph" aria-hidden="true">
                  ✓
                </span>
                <span>
                  Handed to the mail service
                  {messageId ? (
                    <>
                      {' '}
                      as <span className="mono">{messageId}</span>
                    </>
                  ) : (
                    ' — no message id was recorded, so check the run before assuming it left'
                  )}
                  . It is in the Sent archive only when test sends are included.
                </span>
              </div>
            ) : null}

            {done && run && run.status !== 'sent' ? (
              <div className="callout tone-danger" role="alert">
                <span className="callout-glyph" aria-hidden="true">
                  ✕
                </span>
                <span>
                  The run ended <strong>{run.status}</strong> and nothing was sent.{' '}
                  {run.error ??
                    (run.status === 'stale'
                      ? 'The source object was missing or older than the window.'
                      : 'The run record has the detail.')}
                </span>
              </div>
            ) : null}

            {gaveUp && !done ? (
              <div className="callout tone-warn">
                <span className="callout-glyph" aria-hidden="true">
                  ▲
                </span>
                <span>
                  Still running after a couple of minutes. Nothing is wrong with the request —
                  open the run to keep watching it.
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        {refusal ? (
          <div className="callout tone-danger" role="alert">
            <span className="callout-glyph" aria-hidden="true">
              ✕
            </span>
            <div className="stack-tight">
              <span>{refusal}</span>
              {allowedDomains && allowedDomains.length > 0 ? (
                <span className="xs dim">
                  Allowed: {allowedDomains.map((domain) => domain).join(', ')}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export default TestMailDialog;
